import {
  useLayoutEffect,
  useRef,
} from 'react'

import { TERMLY_CODE_SNIPPET_SUPPORT } from 'constants/domIds'


// Returns keyboard focus to the element that opened a view when that view
// closes. We can't hold the triggering element itself: opening a view
// unmounts the one that triggered it, so the node is recreated on close.
// Instead each stack entry records a `restoreFocusKey` and we re-query the
// live DOM for the matching `data-focus-id`.
//
export default function useFocusRestoration({ uiStack }) {
  const prevStackRef = useRef(uiStack)

  useLayoutEffect(() => {
    const prevStack = prevStackRef.current

    prevStackRef.current = uiStack

    if ( uiStack.length >= prevStack.length ) {
      return
    }

    // A view closed. A pop returns to the view underneath, so restore the
    // trigger of the entry that was removed; clearing the stack exits the
    // whole flow, so restore the trigger that opened it in the first place.
    //
    const closedUI = ( uiStack.length === 0 ) ? prevStack[0] : prevStack[prevStack.length - 1]
    const { restoreFocusKey } = closedUI || {}

    if ( !restoreFocusKey ) {
      return
    }

    // Defer to the next frame. Removing the closing view blurs its focused
    // element, and the browser's reset of focus to <body> lands after this
    // commit, so focusing synchronously here would be immediately undone.
    //
    const frameID = requestAnimationFrame(() => {
      restoreFocus(restoreFocusKey)
    })

    return () => {
      cancelAnimationFrame(frameID)
    }
  }, [uiStack])
}

function restoreFocus(restoreFocusKey) {
  // `restoreFocusKey` is interpolated into a selector, so keys must be
  // selector-safe (no `"`/`\`). Today's keys are static literals, hardcoded
  // category names, and numeric ids; escape at the source if that changes.
  //
  const root = document.getElementById(TERMLY_CODE_SNIPPET_SUPPORT)
  const target = root?.querySelector(`[data-focus-id="${ restoreFocusKey }"]`)

  target?.focus()
}
